import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { TransitionBlock } from "@swa-ui/core";

import { useContentLoadingStatus } from "../useContentLoadingStatus";

const WAITING_TIMEOUT = 10000;

/**
 * LayoutSuspense renders its children only after placement content has finished
 * loading.
 */
export const LayoutSuspense = (props) => {
  const {
    animate = false,
    animationToken,
    children,
    className,
    element,
    fallback,
    waitFor = [],
  } = props;
  const { contentHasLoaded } = useContentLoadingStatus();
  const waiting = useRef(false);
  const [doneWaiting, setDoneWaiting] = useState(waitFor.length === 0);
  const [forceDoneWaiting, setForceDoneWaiting] = useState(false);
  const Wrapper = useMemo(() => (animate ? TransitionBlock : element), [animate, element]);
  const fallbackComponent = fallback ?? <div />;

  useEffect(() => {
    const fallbackTimeout = setTimeout(() => {
      if (!shouldRenderChildren()) {
        setForceDoneWaiting(true);
      }
    }, WAITING_TIMEOUT);

    return () => {
      clearTimeout(fallbackTimeout);
    };
  }, []);

  useEffect(() => {
    addWaiting();
  }, []);

  return Wrapper ? <Wrapper {...getProps()}>{renderContent()}</Wrapper> : renderContent();

  function renderContent() {
    return shouldRenderChildren() ? children : fallbackComponent;
  }

  function getProps() {
    return {
      ...(animate && { animationToken: getAnimationToken() }),
      className,
      ...(animate && !fallback && { transitionOut: { duration: 0 } }),
    };
  }

  function getAnimationToken() {
    return `${shouldRenderChildren()}-${animationToken}`;
  }

  function addWaiting() {
    if (!waiting.current && waitFor.length > 0) {
      waiting.current = true;

      Promise.all(waitFor).then(() => {
        waiting.current = false;

        setDoneWaiting(true);
      });
    }
  }

  function shouldRenderChildren() {
    return (doneWaiting && contentHasLoaded) || forceDoneWaiting;
  }
};

LayoutSuspense.displayName = "LayoutSuspense";
LayoutSuspense.propTypes = {
  /**
   * Whether to fade in/out the fallback and child components when the component is done waiting.
   */
  animate: PropTypes.bool,

  /**
   * Token provided to the underlying animation mechanism TransitionBlock to ensure that content loads appropriately
   * on children changes. This is only needed if the `animate` prop is `true` and children are expected to change components.
   */
  animationToken: PropTypes.string,

  /**
   * The content to be rendered once content has loaded and waitFors have been resolved.
   */
  children: PropTypes.node,

  /**
   * The value to apply to the root element of the suspense. If no element is provided
   * then this value is ignored because there will be no root element to which to apply it.
   */
  className: PropTypes.string,

  /**
   * This will be used to wrap the loading and child content within the layout suspense.
   * If this prop is not supplied then no additional markup will be added that wraps the
   * contained content. This prop will be ignored if the `animate` prop is `true`.
   */
  element: PropTypes.string,

  /**
   * The content to show while the LayoutSuspense is waiting for content to load and
   * any waitFor's to resolve.
   */
  fallback: PropTypes.node,

  /**
   * An array of items to wait for before loading the suspended child content. This array
   * can consist of resolved values or Promises.
   */
  waitFor: PropTypes.array,
};
